import { create } from "zustand";

/** One overlapping booking inside the structured 409 the backend emits
 *  when an operator force-moves a SEATED party onto a table that is held
 *  by another reservation overlapping the moved party's window. The
 *  dialog renders one row per conflict so the operator sees exactly who
 *  is in the way before deciding.
 *
 *  Mirrors the backend ``code: table_occupied_override_required``
 *  payload returned by ``POST /api/reservations/{pk}/move-seated/``. */
export interface OccupiedConflict {
  reservation_id: string;
  guest_name: string;
  starts_at: string;
  ends_at: string;
  table_names: string[];
}

export interface OccupiedOverrideContext {
  conflicts: OccupiedConflict[];
  /** Whether shortening the moved party's stay (so it stops before the
   *  clash) is a viable resolution. When false, the "Acortar" action is
   *  disabled. */
  can_shorten: boolean;
  /** ISO instant the moved party would be capped to when shortening, or
   *  null when ``can_shorten`` is false. */
  shorten_to: string | null;
}

/** What the operator chose. ``"force"`` seats anyway accepting the
 *  overlap, ``"shorten"`` caps the moved party before the clash, and
 *  ``null`` means they cancelled the move. */
export type OccupiedOverrideChoice = "force" | "shorten" | null;

interface Pending {
  context: OccupiedOverrideContext;
  resolve: (choice: OccupiedOverrideChoice) => void;
}

interface State {
  pending: Pending | null;
  open: (context: OccupiedOverrideContext) => Promise<OccupiedOverrideChoice>;
  close: (choice: OccupiedOverrideChoice) => void;
}

export const useOccupiedOverrideStore = create<State>((set, get) => ({
  pending: null,
  open: (context) =>
    new Promise<OccupiedOverrideChoice>((resolve) => {
      set({ pending: { context, resolve } });
    }),
  close: (choice) => {
    const { pending } = get();
    if (pending) {
      pending.resolve(choice);
      set({ pending: null });
    }
  },
}));
